import PropTypes from "prop-types";
import React, { useState } from "react";

import { ElementVisibilityProvider, useDeviceInfo } from "@swa-ui/browser";
import { ListScroller, PositionIndicator, Text } from "@swa-ui/core";
import i18n from "@swa-ui/locale";

import { FareOptionDetails } from "../FareOptionDetails/FareOptionDetails";
import styles from "./FareComparisonSelector.module.scss";

/**
 * Creates mock fare details data for testing and development.
 * This is placeholder data for WCM driven content to be implemented as part of
 *  https://southwest.atlassian.net/browse/ATLASMON-853
 */
const createMockFareDetailsList = () => {
  const features = [
    "Two bags fly free",
    "Non-refundable (flight credit)",
    "No change or cancel fees",
    "Free same-day change",
    "Transferable Flight Credit",
    "Refundable",
    "Priority Lane & Express Lane",
    "EarlyBird Check-In",
    "Priority board A1-A1",
  ];
  const fareTypes = [
    {
      accrualPoints: "3426",
      amount: "288.00",
      currencyCode: "USD",
      includedFeatures: 9,
      name: "Business Select",
    },
    {
      accrualPoints: "1982",
      amount: "198.14",
      currencyCode: "USD",
      includedFeatures: 7,
      name: "Anytime",
    },
    {
      accrualPoints: "854",
      amount: "178.00",
      currencyCode: "USD",
      includedFeatures: 5,
      name: "Wanna Get Away Plus",
    },
    {
      accrualPoints: "211",
      amount: "138.00",
      currencyCode: "USD",
      includedFeatures: 3,
      name: "Wanna Get Away",
    },
  ];

  return fareTypes.map(({ includedFeatures, ...otherProps }) => ({
    ...otherProps,
    features: features.map((description, index) => ({
      description,
      included: index < includedFeatures,
    })),
  }));
};
const mockFareDetailsList = createMockFareDetailsList();

/**
 * A component that displays a horizontally scrollable comparison of different fare types and their features.
 */
export const FareComparisonSelector = (props) => {
  const { fareDetailsList = mockFareDetailsList } = props;
  const [visibleIndex, setVisibleIndex] = useState(0);
  const { isTouchDevice } = useDeviceInfo();

  return (
    <ElementVisibilityProvider {...getProviderProps()}>
      <div className={styles.fareComparisonLayout}>
        <Text className={styles.selectFareLabel} color="var(--fare-comparison-selector-text-color)">
          {i18n("FareComparisonSelector__SELECT_FARE_LABEL")}
        </Text>
        <ListScroller {...getListScrollerProps()} />
        {isTouchDevice && (
          <div className={styles.positionIndicatorLayout}>
            <PositionIndicator positionCount={fareDetailsList.length} position={visibleIndex} />
          </div>
        )}
      </div>
    </ElementVisibilityProvider>
  );

  function getProviderProps() {
    return {
      debounceDelay: 250,
      onVisible: handleVisible,
      rememberVisible: false,
    };
  }

  function getListScrollerProps() {
    return {
      "aria-label-current-page": i18n("FareComparisonSelector__ARIA_LABEL_CURRENT_PAGE"),
      "aria-label-focus-change-next": i18n("FareComparisonSelector__ARIA_LABEL_FOCUS_CHANGE_NEXT"),
      "aria-label-focus-change-previous": i18n(
        "FareComparisonSelector__ARIA_LABEL_FOCUS_CHANGE_PREVIOUS"
      ),
      "aria-label-next": i18n("FareComparisonSelector__ARIA_LABEL_NEXT"),
      "aria-label-page-count": i18n("FareComparisonSelector__ARIA_LABEL_PAGE_COUNT"),
      "aria-label-previous": i18n("FareComparisonSelector__ARIA_LABEL_PREVIOUS"),
      itemClassName: styles.listScrollerItem,
      items: getListScrollerItems(),
      layout: "none",
    };
  }

  function getListScrollerItems() {
    return fareDetailsList.map((fareDetails, index) => (
      <FareOptionDetails
        key={fareDetails.fareName}
        fareOptionDetails={fareDetails}
        metadata={{ index }}
      />
    ));
  }

  function handleVisible(elements) {
    const visibleElementIndexes = elements
      .filter((element) => element.visible)
      .map(({ metadata }) => metadata.index);

    if (!visibleElementIndexes?.length || visibleElementIndexes.includes(0)) {
      setVisibleIndex(0);
    } else {
      setVisibleIndex(visibleElementIndexes?.at(-1));
    }
  }
};

FareComparisonSelector.propTypes = {
  /** Array of fare types with a list of features for each one consiting of a feature description
   * and whether or not it is included with the FareType. */
  fareDetailsList: PropTypes.arrayOf(
    PropTypes.shape({
      /* The fare amount. */
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

      /* Points that will be accrued for this fare option. */
      accrualPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

      /* Currency code for the fare amount (e.g., 'USD') */
      currencyCode: PropTypes.string.isRequired,

      /* Name of the fare option. */
      name: PropTypes.string.isRequired,

      /* A list of features for the option of the fare option and a boolean value indicating if the feature is available. */
      features: PropTypes.arrayOf(
        PropTypes.shape({
          description: PropTypes.string.isRequired,
          included: PropTypes.bool.isRequired,
        })
      ).isRequired,
    })
  ),
};
